/**
 * SOAP Note API
 * All axios calls for SOAP notes — fetch, save, shortcuts, templates
 */

import { apiClient } from './config';

/**
 * Fetch a SOAP note by its _id.
 * With includeOrder the answer also has `order` (the linked order as /orderget returns
 * it, or null) and, when the server checked it, `paidInitialAppointment`.
 */
export const fetchSoapNote = async (soapNoteId, { includeOrder = false } = {}) => {
  const response = includeOrder
    ? await apiClient.get(`/get-soap-note/${soapNoteId}`, { params: { include: 'order' } })
    : await apiClient.get(`/get-soap-note/${soapNoteId}`);
  return response.data;
};

/**
 * Create or update a SOAP note.
 * If payload._id is set, updates. Otherwise creates new.
 * Fields left out keep their stored value, so a payload can carry just the changed ones.
 * With expectedUpdatedAt (the note's updatedAt as loaded), a note changed since is not
 * written: the request fails with 409 and { soapNote } (the stored note).
 * Returns { soapNote: { _id, ... } }
 */
export const saveSoapNote = async (payload) => {
  const response = await apiClient.post('/soap-note', payload);
  return response.data;
};

/**
 * Update only the date of a SOAP note
 */
export const updateSoapNoteDate = async (soapNoteId, date) => {
  const response = await apiClient.patch(`/soap-note/${soapNoteId}/date`, { date });
  return response.data;
};

/**
 * Update only the custom name of a SOAP note
 */
export const updateSoapNoteCustomName = async (soapNoteId, customName) => {
  const response = await apiClient.patch(`/soap-note/${soapNoteId}/custom-name`, { customName });
  return response.data;
};

/**
 * Fetch custom shortcut templates (single-dot menu)
 * Backend returns { templates: [...] } — we unwrap to a plain array.
 */
export const fetchShortcuts = async () => {
  const response = await apiClient.get('/api/soap-note-templates/custom');
  return Array.isArray(response.data) ? response.data : (response.data?.templates || []);
};

/**
 * Fetch pre-built section templates by type (double-dot menu / template picker)
 * Backend route: /api/soap-note-templates/:type → returns { templates: [...] }
 * @param {string} type - 'subjective' | 'objective' | 'assessment' | 'custom'
 */
export const fetchSoapTemplates = async (type) => {
  const response = await apiClient.get(`/api/soap-note-templates/${type}`);
  return Array.isArray(response.data) ? response.data : (response.data?.templates || []);
};

/**
 * Check if a SOAP note already exists for a patient on a given date
 */
export const checkSoapNoteExists = async (patientId, date) => {
  const response = await apiClient.get('/check-soapnote-exists', {
    params: { patientId, date },
  });
  return response.data;
};

/**
 * Check if a patient already has a paid initial appointment.
 * Returns { hasPaidInitialAppointment: boolean, patientId: string }
 */
export const checkInitialAppointmentPaid = async (patientId, context = {}) => {
  const response = await apiClient.get(`/check-initial-appointment/${patientId}`, {
    params: {
      currentOrderId: context.currentOrderId,
      currentDate: context.currentDate,
    },
  });
  return response.data;
};
